import {
  formatWorkflowExpressionAst,
  parseWorkflowExpression,
  type WorkflowExpressionAst,
  type WorkflowExpressionReference,
} from '@erp/workflows';
import { getBuilderFunction } from './builderFunctions';

/**
 * The socket expression builder's working model — a socket expression as the
 * builder edits it, which is the parsed AST with two differences:
 *
 * - it can be INCOMPLETE: an `empty` node is a slot nobody has filled yet,
 *   which no expression string can spell; and
 * - field access is folded into its source. `(get (output s k) "f")` is one
 *   `source` node with `field: "f"`, because the builder shows the field
 *   inside the source's argument row rather than as a block of its own.
 *
 * Everything here is pure: the builder component keeps one tree in state and
 * replaces it through these helpers.
 */
export type BuilderNode =
  | { kind: 'empty' }
  | { kind: 'source'; stepId: string; socketId: string; field?: string }
  | { kind: 'literal'; value: string | number }
  | { kind: 'call'; fn: string; args: BuilderNode[] };

/** Argument indices from the root call down to one node. `[]` is the root. */
export type BuilderPath = readonly number[];

export const EMPTY_NODE: BuilderNode = { kind: 'empty' };

const GET_FUNCTION = 'get';

function isOutputAst(ast: WorkflowExpressionAst): boolean {
  return ast.kind === 'call' && ast.name === 'output';
}

function sourceFromOutputAst(
  ast: WorkflowExpressionAst,
  field?: string,
): BuilderNode | null {
  if (ast.kind !== 'call' || ast.name !== 'output') return null;
  const [stepArg, socketArg] = ast.args;
  if (stepArg?.kind !== 'string' || socketArg?.kind !== 'string') return null;
  return {
    kind: 'source',
    stepId: stepArg.value,
    socketId: socketArg.value,
    ...(field === undefined ? {} : { field }),
  };
}

/** A parsed expression as a builder tree. */
export function astToBuilder(ast: WorkflowExpressionAst): BuilderNode {
  if (ast.kind === 'string' || ast.kind === 'number') {
    return { kind: 'literal', value: ast.value };
  }
  const asSource = sourceFromOutputAst(ast);
  if (asSource) return asSource;
  if (ast.name === GET_FUNCTION) {
    const [target, field] = ast.args;
    if (target && isOutputAst(target) && field?.kind === 'string') {
      const folded = sourceFromOutputAst(target, field.value);
      if (folded) return folded;
    }
  }
  return { kind: 'call', fn: ast.name, args: ast.args.map(astToBuilder) };
}

/** The tree as an AST, or `null` while any slot is still empty. */
export function builderToAst(node: BuilderNode): WorkflowExpressionAst | null {
  switch (node.kind) {
    case 'empty':
      return null;
    case 'literal':
      return typeof node.value === 'number'
        ? { kind: 'number', value: node.value }
        : { kind: 'string', value: node.value };
    case 'source': {
      const output: WorkflowExpressionAst = {
        kind: 'call',
        name: 'output',
        args: [
          { kind: 'string', value: node.stepId },
          { kind: 'string', value: node.socketId },
        ],
      };
      return node.field
        ? {
            kind: 'call',
            name: GET_FUNCTION,
            args: [output, { kind: 'string', value: node.field }],
          }
        : output;
    }
    case 'call': {
      const args: WorkflowExpressionAst[] = [];
      for (const arg of node.args) {
        const ast = builderToAst(arg);
        if (!ast) return null;
        args.push(ast);
      }
      return { kind: 'call', name: node.fn, args };
    }
  }
}

/** The tree as stored expression text, or `null` while incomplete. */
export function builderToExpression(node: BuilderNode): string | null {
  const ast = builderToAst(node);
  return ast ? formatWorkflowExpressionAst(ast) : null;
}

export type ParsedBuilder =
  { ok: true; tree: BuilderNode } | { ok: false; error: string };

/** Parse expression text into a tree, keeping the parser's message on failure. */
export function expressionToBuilder(expression: string): ParsedBuilder {
  try {
    return {
      ok: true,
      tree: astToBuilder(parseWorkflowExpression(expression)),
    };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/** Whether any slot in the tree is still empty. */
export function hasEmptySlot(node: BuilderNode): boolean {
  if (node.kind === 'empty') return true;
  return node.kind === 'call' && node.args.some(hasEmptySlot);
}

/** Every source the tree reads, in reading order. */
export function collectSources(
  node: BuilderNode,
): Array<Extract<BuilderNode, { kind: 'source' }>> {
  if (node.kind === 'source') return [node];
  if (node.kind === 'call') return node.args.flatMap(collectSources);
  return [];
}

/**
 * The references to write as plain edges when the tree is nothing but wiring,
 * or `null` when it needs a manual override.
 *
 * Wiring is a single field-less source, or a `collect` of field-less sources
 * into an ARRAY socket — exactly what the save path derives from edges, so an
 * expression of that shape round-trips through edges unchanged. A `collect`
 * into a single-value socket stays an override: the canvas does not let two
 * edges land on one, so it could not be drawn back.
 */
export function wiringReferences(
  node: BuilderNode,
  targetIsArray: boolean,
): WorkflowExpressionReference[] | null {
  if (node.kind === 'source' && !node.field) {
    return [{ stepId: node.stepId, socketId: node.socketId }];
  }
  if (
    node.kind === 'call' &&
    node.fn === 'collect' &&
    targetIsArray &&
    node.args.every((arg) => arg.kind === 'source' && !arg.field)
  ) {
    return collectSources(node).map((source) => ({
      stepId: source.stepId,
      socketId: source.socketId,
    }));
  }
  return null;
}

export function getNodeAt(
  root: BuilderNode,
  path: BuilderPath,
): BuilderNode | undefined {
  let node: BuilderNode | undefined = root;
  for (const index of path) {
    if (node?.kind !== 'call') return undefined;
    node = node.args[index];
  }
  return node;
}

/** `root` with the node at `path` replaced. Unknown paths leave it unchanged. */
export function replaceAt(
  root: BuilderNode,
  path: BuilderPath,
  next: BuilderNode,
): BuilderNode {
  if (path.length === 0) return next;
  if (root.kind !== 'call') return root;
  const [index, ...rest] = path;
  if (index >= root.args.length) return root;
  const args = root.args.slice();
  args[index] = replaceAt(args[index], rest, next);
  return { ...root, args };
}

/** A new call of `fn` with its default number of empty argument slots. */
export function createCall(fn: string): BuilderNode {
  const definition = getBuilderFunction(fn);
  const arity = definition?.minArgs ?? 1;
  return {
    kind: 'call',
    fn,
    args: Array.from({ length: arity }, () => EMPTY_NODE),
  };
}

/** Append an empty slot to the variadic call at `callPath`. */
export function addArgument(
  root: BuilderNode,
  callPath: BuilderPath,
): BuilderNode {
  const call = getNodeAt(root, callPath);
  if (call?.kind !== 'call' || !getBuilderFunction(call.fn)?.variadic) {
    return root;
  }
  return replaceAt(root, callPath, {
    ...call,
    args: [...call.args, EMPTY_NODE],
  });
}

/**
 * Remove the argument at `path`. A variadic call above its minimum loses the
 * row; anywhere else the slot just empties, because a fixed-arity call cannot
 * be missing an argument. Removing the root clears the whole expression.
 */
export function removeArgument(
  root: BuilderNode,
  path: BuilderPath,
): BuilderNode {
  if (path.length === 0) return EMPTY_NODE;
  const parentPath = path.slice(0, -1);
  const index = path[path.length - 1];
  const parent = getNodeAt(root, parentPath);
  if (parent?.kind !== 'call' || index >= parent.args.length) return root;
  const definition = getBuilderFunction(parent.fn);
  const canShrink =
    definition?.variadic === true && parent.args.length > definition.minArgs;
  if (!canShrink) return replaceAt(root, path, EMPTY_NODE);
  return replaceAt(root, parentPath, {
    ...parent,
    args: parent.args.filter((_, argIndex) => argIndex !== index),
  });
}

/** Move the argument at `path` one place earlier (`-1`) or later (`1`). */
export function moveArgument(
  root: BuilderNode,
  path: BuilderPath,
  delta: -1 | 1,
): BuilderNode {
  if (path.length === 0) return root;
  const parentPath = path.slice(0, -1);
  const index = path[path.length - 1];
  const parent = getNodeAt(root, parentPath);
  const target = index + delta;
  if (
    parent?.kind !== 'call' ||
    index >= parent.args.length ||
    target < 0 ||
    target >= parent.args.length
  ) {
    return root;
  }
  const args = parent.args.slice();
  [args[index], args[target]] = [args[target], args[index]];
  return replaceAt(root, parentPath, { ...parent, args });
}

/** Set (or clear, with `undefined`) the field read off the source at `path`. */
export function setSourceField(
  root: BuilderNode,
  path: BuilderPath,
  field: string | undefined,
): BuilderNode {
  const node = getNodeAt(root, path);
  if (node?.kind !== 'source') return root;
  const trimmed = field?.trim();
  const { field: _previous, ...rest } = node;
  return replaceAt(root, path, trimmed ? { ...rest, field: trimmed } : rest);
}

export function pathKey(path: BuilderPath): string {
  return path.length === 0 ? 'root' : path.join('.');
}

export function samePath(
  a: BuilderPath | null | undefined,
  b: BuilderPath | null | undefined,
): boolean {
  if (!a || !b || a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}
